import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Popconfirm, Select, Table, Tag, Tooltip, message } from "antd";
import {
  ReloadOutlined, PlusOutlined, TeamOutlined, MailOutlined, ShopOutlined,
  PushpinOutlined, PushpinFilled, FunnelPlotOutlined, CalendarOutlined,
} from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import {
  getPractices, sendFollowUp, deletePractice, pinPractice, unpinPractice,
  reactivatePractice, reactivateOffice,
} from "./controller";
import InviteCustomerModal from "./InviteCustomerModal";
import OfficeNotesPopover from "./OfficeNotesPopover";
import CrmInvites from "./CrmInvites";
import CrmLeads from "./CrmLeads";
import CrmToday from "./CrmToday";
import LeadDetailDrawer from "./LeadDetailDrawer";
import PracticeDetailDrawer from "./PracticeDetailDrawer";
import OfficeDetailDrawer from "./OfficeDetailDrawer";
import MarkDormantWizard from "./MarkDormantWizard";
import { PracticeTagEditor } from "./LeadTagPicker";
import { tourTag } from "./tourLabel";
import localDay from "./lib/localDay";
import "./crm.css";
import "./dormant.css";

const STAGE_OPTIONS = [
  { label: "All stages", value: "" },
  { label: "Not started", value: "not_started" },
  { label: "Signed up", value: "signed_up" },
  { label: "Setting up practice", value: "practice_setup" },
  { label: "Practice created", value: "practice_created" },
  { label: "Office set up", value: "office_setup" },
  { label: "Banking", value: "banking" },
  { label: "Inviting users", value: "inviting_users" },
  { label: "PMS integration setup", value: "sikka_setup" },
  { label: "Portal credentials", value: "portal_credentials" },
  { label: "Procedure codes", value: "procedure_code_defaults" },
  { label: "Subscription", value: "subscription" },
  { label: "Completed", value: "completed" },
];

// Outstanding work, not history: a completed task is nothing to chase, so it
// never brings its practice into these results.
const TASK_FILTER_OPTIONS = [
  { label: "Any tasks", value: "" },
  { label: "Has open tasks", value: "open" },
  { label: "Has overdue tasks", value: "overdue" },
];

// Dormant is per office; a practice reads dormant only when every office is.
// "Active" (the server default) hides dormant offices so the working list
// stays the list of customers we are actually serving.
const PRACTICE_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Dormant", value: "dormant" },
  { label: "All", value: "all" },
];

function initials(name) {
  if (!name) return "—";
  const p = name.trim().split(/\s+/);
  return ((p[0][0] || "") + (p[1] ? p[1][0] : "")).toUpperCase();
}

// The wizard wants a flat target either way; a practice row carries its offices
// (with their dormant flags), an office child row IS the one office.
function dormantTargetFromRow(r) {
  if (r.__isOffice) {
    return { kind: "office", id: r.id, name: r.name, offices: [{ id: r.id, name: r.name, dormant: !!r.dormant }] };
  }
  return {
    kind: "practice",
    id: r.id,
    name: r.name,
    offices: (Array.isArray(r.offices) ? r.offices : []).map((o) => ({ id: o.id, name: o.name, dormant: !!o.dormant })),
  };
}

export default function CrmPage() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [tasksFilter, setTasksFilter] = useState("");
  const [practiceStatus, setPracticeStatus] = useState("active");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(200);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [officeDetail, setOfficeDetail] = useState(null); // {officeId, practiceId, tab?, messageId?}
  // Today is the landing tab: the page opens on what needs doing, one click
  // from the customer table it used to open on.
  const [activeTab, setActiveTab] = useState("today");
  const [inviteCount, setInviteCount] = useState(0);
  const [reloadInvites, setReloadInvites] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [leadsTick, setLeadsTick] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [todayCounts, setTodayCounts] = useState(null);
  const [todayTick, setTodayTick] = useState(0);
  // Stable on purpose: an inline function here once made the Today tab refetch
  // on every render of this page (see CrmToday's `report`).
  const onTodayCount = useCallback((badge, counts) => {
    setTodayCount(badge);
    setTodayCounts(counts || null);
  }, []);
  const [todayLead, setTodayLead] = useState(null);       // {id, messageId?}: lead drawer opened from Today
  const [inviteLead, setInviteLead] = useState(null);     // Today's "Invite" on an untouched lead
  const [invitesFocus, setInvitesFocus] = useState("");   // email to search for when Today opens the Invites tab
  const [dormantTarget, setDormantTarget] = useState(null); // wizard target, or null when closed

  // `silent` refetches without flashing the table's spinner — used when a drawer
  // closes, where the rows are already on screen and only some cells may differ.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await getPractices({
        page,
        pageSize,
        search: search || undefined,
        stage: stage || undefined,
        tasks: tasksFilter || undefined,
        status: practiceStatus,
        date: localDay(),
      });
      setStatus(res.status);
      if (res.status >= 200 && res.status < 300 && res.json) {
        setRows(Array.isArray(res.json.results) ? res.json.results : []);
        setCount(typeof res.json.count === "number" ? res.json.count : 0);
      } else {
        setRows([]);
        setCount(0);
      }
    } catch (e) {
      setRows([]);
      setCount(0);
      setStatus(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, search, stage, tasksFilter, practiceStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const reminder = async (row) => {
    const res = await sendFollowUp({ practiceId: row.id });
    if (res.status >= 200 && res.status < 300) {
      message.success("Reminder sent.");
    } else {
      message.error((res.json && res.json.detail) || "Failed to send reminder.");
    }
  };

  // Pin/unpin a practice so it floats to the top of the table (team-wide).
  const togglePin = async (row) => {
    const res = row.pinned ? await unpinPractice(row.id) : await pinPractice(row.id);
    if (res.status >= 200 && res.status < 300) {
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't update the pin.");
    }
  };

  // Soft-delete a clinic — it disappears from the list but is kept in the DB.
  const removePractice = async (row) => {
    const res = await deletePractice(row.id);
    if (res.status >= 200 && res.status < 300) {
      message.success(`${row.name || "Clinic"} removed.`);
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't remove the clinic.");
    }
  };

  // Bring a dormant practice (every office) or one office back. No wizard: the
  // note and tags from when it went quiet stay on record, nothing new to record.
  const reactivate = async (row) => {
    const res = row.__isOffice ? await reactivateOffice(row.id) : await reactivatePractice(row.id);
    if (res.status >= 200 && res.status < 300) {
      message.success(`${row.name || (row.__isOffice ? "Office" : "Practice")} reactivated.`);
      load({ silent: true });
    } else {
      message.error((res.json && res.json.detail) || "Couldn't reactivate.");
    }
  };

  // Multi-office practices become expandable parent rows with per-office child
  // rows; single-office practices stay as one row (clicking opens that office).
  const treeData = useMemo(
    () =>
      rows.map((r) => {
        const base = { ...r, __rowKey: `p${r.id}` };
        if (r.office_count > 1 && Array.isArray(r.offices) && r.offices.length) {
          base.children = r.offices.map((o) => ({
            ...o, __isOffice: true, __practiceId: r.id, __rowKey: `o${o.id}`,
          }));
        }
        return base;
      }),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        title: "Practice / Office",
        render: (_, r) =>
          r.__isOffice ? (
            <OfficeNotesPopover officeId={r.id} officeName={r.name}>
              <div className="crm-cell-practice">
                <span className="crm-avatar-sm office"><ShopOutlined /></span>
                <div className="crm-cell-stack">
                  <div className="crm-name">
                    {r.name}
                    {r.dormant ? <span className="crmd-pill">Dormant</span> : null}
                  </div>
                  <div className="crm-sub">{r.email || "—"}{r.office_id ? " · PMS ID " + r.office_id : ""}{r.practice_management_system ? " · " + r.practice_management_system : ""}</div>
                </div>
              </div>
            </OfficeNotesPopover>
          ) : (
            (() => {
              // A single-office practice is shown as one (non-expandable) row that
              // functionally IS that office (row click opens the office drawer), so
              // give it the same notes-hover as the explicit office child rows.
              const singleOffice =
                r.office_count <= 1 && Array.isArray(r.offices) && r.offices[0];
              const cell = (
                <div className="crm-cell-practice">
                  <span
                    className={`crm-pin-toggle${r.pinned ? " is-pinned" : ""}`}
                    role="button"
                    title={r.pinned ? "Unpin from top" : "Pin to top"}
                    onClick={(e) => { e.stopPropagation(); togglePin(r); }}
                  >
                    {r.pinned ? <PushpinFilled /> : <PushpinOutlined />}
                  </span>
                  <span className="crm-avatar-sm">{initials(r.name)}</span>
                  <div className="crm-cell-stack">
                    <div className="crm-name">
                      {r.name}
                      {r.dormant ? <span className="crmd-pill">Dormant</span> : null}
                    </div>
                    <div className="crm-sub">{r.email || "—"}{r.phone ? " · " + r.phone : ""}</div>
                  </div>
                </div>
              );
              return singleOffice ? (
                <OfficeNotesPopover officeId={singleOffice.id} officeName={r.name}>
                  {cell}
                </OfficeNotesPopover>
              ) : (
                cell
              );
            })()
          ),
      },
      {
        title: "Owner",
        render: (_, r) =>
          r.__isOffice ? (
            <span className="crm-sub">Office</span>
          ) : r.owner ? (
            <div className="crm-cell-stack">
              <div className="crm-cell-strong">{r.owner.name}</div>
              <div className="crm-sub">{r.owner.email}</div>
            </div>
          ) : (
            <span className="crm-sub">—</span>
          ),
      },
      {
        // Lead tags belong to the practice's lead, so only practice rows carry
        // them. They ride along on the row (r.lead) — no request per row.
        title: "Tags",
        width: 190,
        render: (_, r) =>
          r.__isOffice ? (
            <span className="crm-sub">—</span>
          ) : (
            <PracticeTagEditor
              practiceId={r.id}
              tags={(r.lead && Array.isArray(r.lead.tags) && r.lead.tags) || []}
              onSaved={() => load({ silent: true })}
            />
          ),
      },
      {
        title: "Offices",
        dataIndex: "office_count",
        width: 88,
        align: "center",
        render: (v, r) => (r.__isOffice ? <span className="crm-sub">—</span> : <span className="crm-pill-num">{v}</span>),
      },
      {
        // "Onboarding" now reflects chatbot-tour progress (the "Ready" column already
        // conveys registration readiness). Complete when the tour is done, else
        // "In progress/Exited X/TOTAL".
        title: "Onboarding",
        width: 162,
        render: (_, r) => {
          const t = tourTag(r.tour);
          return <Tag color={t.color}>{t.text}</Tag>;
        },
      },
      {
        title: "Ready",
        width: 116,
        align: "center",
        render: (_, r) => {
          if (!r.__isOffice && r.office_count > 1 && r.rollup) {
            const rl = r.rollup;
            return (
              <span className={`crm-ready-pill ${rl.ready >= rl.total ? "full" : "partial"}`}>
                {rl.ready}/{rl.total} offices
              </span>
            );
          }
          const rd = r.readiness || {};
          const total = typeof rd.total === "number" ? rd.total : 7;
          const done = typeof rd.done === "number" ? rd.done : 0;
          const pill = <span className={`crm-ready-pill ${done >= total ? "full" : "partial"}`}>{done} / {total}</span>;
          // Steps switched off when they registered aren't counted (N/A).
          return rd.not_applicable > 0 ? (
            <Tooltip title={rd.not_applicable === 1
              ? "1 step wasn't part of their onboarding and isn't counted"
              : `${rd.not_applicable} steps weren't part of their onboarding and aren't counted`}>
              {pill}
            </Tooltip>
          ) : pill;
        },
      },
      {
        // Which portal logins the office has handed over. Only meaningful per
        // office — a multi-office practice row has no single set of logins.
        title: "Portals",
        width: 110,
        align: "center",
        render: (_, r) => {
          const pc = r.portal_credentials;
          if (!r.__isOffice || !pc || !pc.total) return <span className="crm-sub">—</span>;
          const full = pc.collected >= pc.total;
          return (
            <span className={`crm-ready-pill ${full ? "full" : "partial"}`}>
              {pc.collected} / {pc.total}
            </span>
          );
        },
      },
      {
        title: "Tasks",
        width: 210,
        render: (_, r) => {
          // Office rows carry their own tasks; a practice row pools its offices'
          // (each task keeps the office it belongs to, since that is what opens).
          const tasks = r.open_tasks || [];
          if (tasks.length === 0) return <span className="crm-sub">—</span>;
          return (
            <div className="crm-cell-stack" onClick={(e) => e.stopPropagation()}>
              {tasks.map((t) => {
                // Stamped by the server against the day we sent (localDay), so
                // this cell and the Today tab can never disagree on "overdue".
                const overdue = !!t.is_overdue;
                return (
                  <Tooltip
                    key={t.id}
                    title={`${t.body}${t.office_name ? " — " + t.office_name : ""}${
                      t.assignee ? " · " + (t.assignee.name || t.assignee.email) : ""
                    }`}
                  >
                    <div
                      className="crm-task-chip"
                      role="button"
                      onClick={() =>
                        setOfficeDetail({
                          officeId: t.office_id,
                          practiceId: r.__isOffice ? r.__practiceId : r.id,
                          tab: "notes",
                        })
                      }
                      style={{ cursor: "pointer", lineHeight: 1.3, marginBottom: 2 }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: 190,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          verticalAlign: "bottom",
                        }}
                      >
                        {t.body}
                      </span>
                      {t.due_date ? (
                        <div className="crm-sub" style={{ color: overdue ? "#cf1322" : undefined }}>
                          due {t.due_date}
                          {overdue ? " · overdue" : ""}
                        </div>
                      ) : null}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        },
      },
      {
        title: "Subscription",
        width: 168,
        render: (_, r) => {
          const sub = r.subscription || {};
          if (!sub.tier_title) return <span className="crm-sub">—</span>;
          const status = r.__isOffice
            ? (sub.trial ? "trial · " + sub.trial.status : "active")
            : sub.subscription_status;
          return (
            <div className="crm-cell-stack">
              <div className="crm-cell-strong">{sub.tier_title}</div>
              {status ? <div className="crm-sub">{status}</div> : null}
            </div>
          );
        },
      },
      {
        title: "Active",
        width: 100,
        align: "center",
        render: (_, r) =>
          r.__isOffice ? (
            <span className="crm-sub">—</span>
          ) : (
            <span className={`crm-status-pill ${r.active ? "on" : "off"}`}>{r.active ? "Active" : "Inactive"}</span>
          ),
      },
      {
        title: "",
        key: "actions",
        width: 340,
        align: "right",
        render: (_, r) => {
          // A dormant row offers the way back; anything else offers the way out.
          const dormantAction = r.dormant ? (
            <Popconfirm
              title={r.__isOffice ? "Reactivate this office?" : "Reactivate this practice?"}
              description={r.__isOffice ? "It returns to the active list." : "Every office returns to the active list."}
              okText="Reactivate"
              cancelText="Cancel"
              onConfirm={() => reactivate(r)}
            >
              <Button size="small">Reactivate</Button>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => setDormantTarget(dormantTargetFromRow(r))}>
              Mark dormant
            </Button>
          );
          return (
            <div className="crm-row-actions" onClick={(e) => e.stopPropagation()}>
              {r.__isOffice ? (
                <>
                  <Button size="small" type="primary" ghost onClick={() => setOfficeDetail({ officeId: r.id, practiceId: r.__practiceId })}>
                    Open
                  </Button>
                  {dormantAction}
                </>
              ) : (
                <>
                  <Button size="small" type="primary" ghost onClick={() => setDetailId(r.id)}>
                    Practice Details
                  </Button>
                  <Button size="small" onClick={() => reminder(r)}>
                    Reminder
                  </Button>
                  {dormantAction}
                  <Popconfirm
                    title="Remove this clinic?"
                    description="It will no longer appear in this list."
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => removePractice(r)}
                  >
                    <Button size="small" danger>Delete</Button>
                  </Popconfirm>
                </>
              )}
            </div>
          );
        },
      },
    ],
    // Every handler above closes over `load` (directly or via reminder/togglePin/
    // reactivate, which are re-created each render but only ever call load and
    // setters), so `load` is the one dependency that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load]
  );

  const header = (
    <div className="crm-page-head">
      <div className="crm-page-head-left">
        <span className="crm-page-icon"><TeamOutlined /></span>
        <div>
          <div className="crm-page-title">Practices</div>
          <div className="crm-page-sub">What&rsquo;s on your plate, plus practices, leads, onboarding &amp; invites across the platform</div>
        </div>
      </div>
      <div className="crm-page-actions">
        <Button icon={<PlusOutlined />} type="primary" onClick={() => setInviteOpen(true)}>
          Invite customer
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            if (activeTab === "today") setTodayTick((x) => x + 1);
            else if (activeTab === "invites") setReloadInvites((x) => x + 1);
            else if (activeTab === "leads") setLeadsTick((x) => x + 1);
            else load();
          }}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
    </div>
  );

  if (status === 403) {
    return (
      <DashboardLayout>
        <div className="crm-page">
          {header}
          <div className="crm-table-card crm-403">
            <Empty description="Super-admin access only." />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="crm-page">
        {header}

        <div className="crm-tabs" role="tablist" aria-label="CRM views">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "today"}
            className={`crm-tab${activeTab === "today" ? " is-active" : ""}`}
            onClick={() => setActiveTab("today")}
            title={todayCounts
              ? `${todayCounts.overdue || 0} overdue · ${todayCounts.today || 0} due today`
              : undefined}
          >
            <CalendarOutlined /> Today <span className="crm-tab-badge">{todayCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "practices"}
            className={`crm-tab${activeTab === "practices" ? " is-active" : ""}`}
            onClick={() => setActiveTab("practices")}
          >
            <TeamOutlined /> Practices <span className="crm-tab-badge">{count}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "leads"}
            className={`crm-tab${activeTab === "leads" ? " is-active" : ""}`}
            onClick={() => setActiveTab("leads")}
          >
            <FunnelPlotOutlined /> Leads <span className="crm-tab-badge">{leadCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "invites"}
            className={`crm-tab${activeTab === "invites" ? " is-active" : ""}`}
            onClick={() => setActiveTab("invites")}
          >
            <MailOutlined /> Invites <span className="crm-tab-badge">{inviteCount}</span>
          </button>
        </div>

        {/* Mounted like the other panels; switching tabs costs no refetch. */}
        <div style={{ display: activeTab === "today" ? "block" : "none" }}>
          <CrmToday
            reloadTick={todayTick}
            onCount={onTodayCount}
            onOpenOffice={(officeId, practiceId, tab, messageId) =>
              setOfficeDetail({ officeId, practiceId, tab, messageId })}
            onOpenLead={(id, messageId) => setTodayLead(id ? { id, messageId } : null)}
            onGoToInvites={(email) => { setInvitesFocus(email || ""); setActiveTab("invites"); }}
            onInviteLead={setInviteLead}
            onInviteCustomer={() => setInviteOpen(true)}
          />
        </div>

        <div style={{ display: activeTab === "practices" ? "block" : "none" }}>
          <div className="crm-toolbar">
            <Input.Search
              allowClear
              placeholder="Search practice name or email"
              style={{ maxWidth: 340 }}
              onSearch={(v) => {
                setPage(1);
                setSearch(v);
              }}
            />
            <Select
              style={{ minWidth: 200 }}
              value={stage}
              options={STAGE_OPTIONS}
              onChange={(v) => {
                setPage(1);
                setStage(v);
              }}
            />
            <Select
              style={{ minWidth: 180 }}
              value={tasksFilter}
              options={TASK_FILTER_OPTIONS}
              onChange={(v) => {
                setPage(1);
                setTasksFilter(v);
              }}
            />
            <Select
              style={{ minWidth: 130 }}
              value={practiceStatus}
              options={PRACTICE_STATUS_OPTIONS}
              onChange={(v) => {
                setPage(1);
                setPracticeStatus(v);
              }}
            />
          </div>

          <div className="crm-table-card">
            <Table
              rowKey={(r) => r.__rowKey}
              columns={columns}
              dataSource={treeData}
              loading={loading}
              rowClassName={(r) => (r.__isOffice ? "crm-row crm-row-office" : "crm-row")}
              expandable={{ rowExpandable: (r) => Array.isArray(r.children) && r.children.length > 0 }}
              onRow={(r) => ({
                onClick: () => {
                  if (r.__isOffice) {
                    setOfficeDetail({ officeId: r.id, practiceId: r.__practiceId });
                    return;
                  }
                  if (r.office_count > 1) {
                    setDetailId(r.id);
                    return;
                  }
                  const off = Array.isArray(r.offices) && r.offices[0];
                  if (off) setOfficeDetail({ officeId: off.id, practiceId: r.id });
                  else setDetailId(r.id);
                },
              })}
              locale={{ emptyText: <Empty description="No practices found." /> }}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: count,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                onChange: (nextPage, nextSize) => {
                  setPage(nextPage);
                  setPageSize(nextSize);
                },
              }}
            />
          </div>
        </div>

        {/* Kept mounted like the other panels so switching tabs costs no refetch. */}
        <div style={{ display: activeTab === "leads" ? "block" : "none" }}>
          <CrmLeads
            reloadTick={leadsTick}
            onCount={setLeadCount}
            onOpenPractice={(id) => setDetailId(id)}
            onOpenOffice={(officeId, practiceId) => setOfficeDetail({ officeId, practiceId })}
          />
        </div>

        <div style={{ display: activeTab === "invites" ? "block" : "none" }}>
          <CrmInvites reloadTick={reloadInvites} onCount={setInviteCount} initialSearch={invitesFocus} />
        </div>

        <MarkDormantWizard
          open={!!dormantTarget}
          target={dormantTarget}
          onClose={() => setDormantTarget(null)}
          onDone={() => {
            load({ silent: true });
            setTodayTick((x) => x + 1);   // the wizard can leave a follow-up task behind
          }}
        />

        <InviteCustomerModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            setReloadInvites((x) => x + 1);
            // From Today, stay on Today — the new invite shows up in the list;
            // from anywhere else, land on the Invites tab as before.
            if (activeTab === "today") setTodayTick((x) => x + 1);
            else setActiveTab("invites");
          }}
        />
        {inviteLead ? (
          <InviteCustomerModal
            open
            onClose={() => setInviteLead(null)}
            onCreated={() => {
              setReloadInvites((x) => x + 1);
              setLeadsTick((x) => x + 1);
              setTodayTick((x) => x + 1);
            }}
            initial={{
              email: (inviteLead.raw && inviteLead.raw.email) || "",
              practiceName: inviteLead.title || "",
              contactPersonName: "",
              contactPhone: (inviteLead.raw && inviteLead.raw.phone) || "",
            }}
            leadId={inviteLead.id}
          />
        ) : null}
        <PracticeDetailDrawer
          practiceId={detailId}
          onClose={() => {
            setDetailId(null);
            load({ silent: true });
            setTodayTick((x) => x + 1);
          }}
          onOpenOffice={(officeId, tab) => setOfficeDetail({ officeId, practiceId: detailId, tab })}
        />
        <LeadDetailDrawer
          leadId={todayLead ? todayLead.id : null}
          initialMessageId={todayLead ? todayLead.messageId : undefined}
          onClose={() => {
            setTodayLead(null);
            setLeadsTick((x) => x + 1);
            setTodayTick((x) => x + 1);
          }}
          onChanged={() => setLeadsTick((x) => x + 1)}
          onOpenPractice={(id) => setDetailId(id)}
          onOpenOffice={(officeId, practiceId) => setOfficeDetail({ officeId, practiceId })}
        />
        <OfficeDetailDrawer
          officeId={officeDetail ? officeDetail.officeId : null}
          practiceId={officeDetail ? officeDetail.practiceId : null}
          initialTab={officeDetail ? officeDetail.tab : undefined}
          initialMessageId={officeDetail ? officeDetail.messageId : undefined}
          onClose={() => {
            setOfficeDetail(null);
            // A task added or ticked off in the drawer changes the Tasks column;
            // so do contacts, the trial cap and completing setup. Refetch rather
            // than trying to mirror each mutation back into the row by hand.
            load({ silent: true });
            setTodayTick((x) => x + 1);
          }}
        />
      </div>
    </DashboardLayout>
  );
}
